import { useEffect } from 'react';
import {
  Alert,
  Anchor,
  Box,
  Button,
  Card,
  FormField,
  Heading,
  Label,
  Paragraph,
  Text,
  Textarea,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import { Icon } from '@zillow/constellation-10';
import { IconLockOpenFilled } from '@zillow/constellation-icons';
import { Controller } from 'react-hook-form';
import { LoadingText } from '@zg-rentals/loading-components';
import { useApplicationFee } from '../../../../hooks/useApplicationFee';
import { BlockLoadingMask } from '../../../../styles/css-components';
import { GetRentalApplicationDocument, UserStatusType } from '../../../../graphql/queries';
import type { UserStatus } from '../../../../graphql/queries';
import {
  getIsIdentityVerified,
  getIsPaymentValid,
  getIsReAuthAllowed,
  getPaymentExpirationDate,
} from '../../../../utils/user-statuses';
import { useApolloClient } from '@apollo/client';
import { useRouter } from 'next/router';
import type { ReportType } from '../../../../models/reports';
import { useCappsScreeningReportsWebApiHandler_createReports } from '@zg-rentals/ts-capps-client-react-query';
import { APPLICATIONS_FAQ_URL, getEditRoute } from '../../../../constants/urls';
import { FIELD_IDS, STEP_IDS } from '../../../../constants/wizard';
import { PaymentForm } from '../../../Payment/PaymentForm';
import { ReportAlert } from '../../../Reports/BackgroundReport/BackgroundReportAlert';
import { useGetReportsForApplication } from '../../../../hooks/useGetReports';
import { logError } from '@zg-rentals/log-error';
import { getHasNonExpiredReports, transformReportData } from '../../../../utils/reports/report-utils';
import { formatDate } from '../../../../utils/formatters';
import { CreditReportOverview } from '../../../Reports/ReportCards/CreditReportOverview';
import { BackgroundReportOverview } from '../../../Reports/ReportCards/BackgroundReportOverview';
import { REQUESTING_CLIENT } from '../../../../constants/api';

type Props = {
  applicationId: string;
  userStatuses: Array<UserStatus>;
  onPersist: () => Promise<void>;
};

export function PaymentAndReportsStep({ applicationId, userStatuses, onPersist }: Props) {
  const router = useRouter();
  const { enqueueToast } = useToast();
  const isIdVerified = getIsIdentityVerified(userStatuses);
  const isPaymentValid = getIsPaymentValid(userStatuses);

  useEffect(() => {
    if (!isIdVerified) {
      enqueueToast(<Toast tone="info">Identity verification is required before purchasing screening reports</Toast>);
      router.push(
        getEditRoute({
          applicationId,
          stepId: STEP_IDS.ID_VERIFICATION,
          nextStepId: STEP_IDS.PAYMENT_AND_REPORTS,
        }),
      );
    }
  }, [isIdVerified]);

  return isIdVerified ? (
    <>
      <Heading level={2} textStyle="heading-sm" fontFamily="heading-alt" css={{ marginBottom: 'default' }}>
        Screening reports
      </Heading>
      {!isPaymentValid ? (
        <PaymentForm applicationId={applicationId} />
      ) : (
        <PaymentValid userStatuses={userStatuses} applicationId={applicationId} onPersist={onPersist} />
      )}
    </>
  ) : null;
}

function PaymentValid({ applicationId, userStatuses, onPersist }: Props) {
  const expirationDate = getPaymentExpirationDate(userStatuses);
  return (
    <>
      {expirationDate && (
        <Paragraph css={{ marginBottom: 'default' }}>
          Your screening reports are valid until {formatDate(expirationDate)}
        </Paragraph>
      )}
      <Reports userStatuses={userStatuses} applicationId={applicationId} onPersist={onPersist} />
    </>
  );
}

function Reports({ userStatuses, applicationId }: Props) {
  const router = useRouter();
  const createReportsMutation = useCappsScreeningReportsWebApiHandler_createReports();
  const { data, isPending } = useGetReportsForApplication();
  const reportData = transformReportData(data);
  const apolloClient = useApolloClient();
  const hasNonExpiredReports = getHasNonExpiredReports(userStatuses);

  async function createReports() {
    try {
      await createReportsMutation.mutateAsync({
        postData: {
          requestingClient: REQUESTING_CLIENT,
        },
      });
      await apolloClient.refetchQueries({ include: [GetRentalApplicationDocument] });
    } catch (e) {
      logError({ error: e, context: 'Reports - createReportsMutation error' });
    }
  }

  useEffect(() => {
    if (!hasNonExpiredReports) {
      createReports();
    }
  }, [hasNonExpiredReports]);

  function navigateToReport(reportType: ReportType) {
    const { pathname, query } = router;
    router.push({ pathname, query: { ...query, reportType } });
  }

  function navigateToIDVStep() {
    router.push(
      getEditRoute({
        applicationId,
        stepId: STEP_IDS.ID_VERIFICATION,
        nextStepId: STEP_IDS.PAYMENT_AND_REPORTS,
      }),
    );
  }

  const isReAuthAllowed = getIsReAuthAllowed(userStatuses);

  return !createReportsMutation.isError ? (
    <BlockLoadingMask loading={!hasNonExpiredReports || isPending}>
      <ReportAlert reportData={reportData} css={{ marginBottom: 'default' }} />
      {isReAuthAllowed && (
        <Button
          tone="brand"
          emphasis="filled"
          type="button"
          fluid
          css={{ marginBottom: 'default' }}
          onClick={navigateToIDVStep}
        >
          Edit information
        </Button>
      )}
      <Card elevated={false} tone="neutral">
        <CreditReportOverview
          creditReport={reportData?.creditReport}
          userStatuses={userStatuses}
          onView={() => navigateToReport(UserStatusType.creditReport)}
        />
        <Controller
          name={FIELD_IDS.CREDIT_REPORT_COMMENT}
          render={({ field: { onChange, value, onBlur } }) => (
            <FormField
              css={{ marginTop: 'default' }}
              label={<Label>Add anything you’d like to include about your credit report</Label>}
              control={
                <Textarea
                  maxLength={255}
                  value={value}
                  css={{ resize: 'vertical' }}
                  onChange={(e) => onChange(e.target.value)}
                  onBlur={onBlur}
                />
              }
              optional
            />
          )}
        />
      </Card>
      <Card elevated={false} tone="neutral" css={{ marginTop: 'default' }}>
        <BackgroundReportOverview
          backgroundReport={reportData?.backgroundReport}
          userStatuses={userStatuses}
          onView={() => navigateToReport(UserStatusType.backgroundReport)}
        />
        <Controller
          name={FIELD_IDS.BACKGROUND_REPORT_COMMENT}
          render={({ field: { onChange, value, onBlur } }) => (
            <FormField
              css={{ marginTop: 'default' }}
              label={<Label>Add anything you’d like to include about your background check</Label>}
              control={
                <Textarea
                  value={value}
                  css={{ resize: 'vertical' }}
                  maxLength={255}
                  onChange={(e) => onChange(e.target.value)}
                  onBlur={onBlur}
                />
              }
              optional
            />
          )}
        />
      </Card>
    </BlockLoadingMask>
  ) : (
    <Alert tone="critical">
      <Text>
        There was an error generating your reports. If the problem continues, contact{' '}
        <Anchor href={APPLICATIONS_FAQ_URL} target="_blank" rel="noopener noreferrer">
          customer support
        </Anchor>
        .
      </Text>
    </Alert>
  );
}

export function PaymentAndReportsStepTip() {
  return <PaymentsAndReportsTipNoUpsell />;
}

function PaymentsAndReportsTipNoUpsell() {
  const router = useRouter();
  const { applicationId } = router.query as { applicationId: string };
  const { feeDisplay, isLoading: isFeeLoading } = useApplicationFee({ applicationId });
  return (
    <Card elevated={false} tone="neutral">
      <Icon css={{ marginBottom: 'default' }} render={<IconLockOpenFilled />} size="xl" />

      <Heading level={2} textStyle="heading-sm" fontFamily="heading-alt" css={{ marginBottom: 'default' }}>
        Credit & background reports
      </Heading>
      <Paragraph>
        Your{' '}
        <LoadingText isLoading={isFeeLoading} loadingWidth="48px">
          {feeDisplay}
        </LoadingText>{' '}
        fee allows you to apply to an unlimited number of participating rentals for 30 days, and also includes your
        credit and background reports! This will be a soft pull on your credit, so your credit score won’t be affected.
      </Paragraph>
    </Card>
  );
}

export const ReAuthRecommendation = {
  condition: ({ userStatuses }: { userStatuses: Array<UserStatus> }) => getIsReAuthAllowed(userStatuses),
  body: <ReAuthRecommendationBody />,
  title: 'Background check unavailable',
};

function ReAuthRecommendationBody() {
  const router = useRouter();
  const { applicationId } = router.query as { applicationId: string };

  function navigateToIDVStep() {
    router.push(
      getEditRoute({
        applicationId,
        stepId: STEP_IDS.ID_VERIFICATION,
        nextStepId: STEP_IDS.PAYMENT_AND_REPORTS,
      }),
    );
  }

  return (
    <>
      <Paragraph>
        A Social Security number or ITIN is required to pull your background check. Most rental managers will want to
        see a background check with your application.
      </Paragraph>
      <Box css={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'default' }}>
        <Button tone="brand" emphasis="filled" type="button" fluid onClick={navigateToIDVStep}>
          Edit information
        </Button>
      </Box>
    </>
  );
}
